import React, { useState, useEffect, useRef } from 'react';
import { message, Form, Input, Button, Card, Tooltip, DatePicker, Space, Typography, Select } from 'antd';
import { SendOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import '../../styles/messages/MessageReply.css';
import { useAuth } from '../../hooks/useAuth';
import { useBecomeGuest } from '../../contexts/BecomeGuestContext';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import MessageEditor from './MessageEditor';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import AccessCodeModal from '../common/AccessCodeModal';
import { useTranslation } from 'react-i18next';
import { isConvertible } from '../../utility/fileTypes';

const { Text } = Typography;

const MessageReply = () => {
    const { t } = useTranslation();
    const { setCanBecomeGuest } = useBecomeGuest();
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [messageData, setMessageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [requiresAccessCode, setRequiresAccessCode] = useState(false);
    const { unique_identifier } = useParams();
    const navigate = useNavigate();
    const messageEditorRef = useRef(null);
    const { isLoggedIn, isLoading: authLoading } = useAuth();
    const [isFreeAccount, setIsFreeAccount] = useState(false);
    const location = useLocation();

    useEffect(() => {
        if (isLoggedIn) {
            setIsFreeAccount(false);
            return;
        }
        let cancelled = false;
        fetch('/api/contacts-mailbox/status/', { credentials: 'include' })
            .then((res) => res.json())
            .then((data) => {
                if (!cancelled) setIsFreeAccount(data.is_free_account === true);
            })
            .catch(() => {
                if (!cancelled) setIsFreeAccount(false);
            });
        return () => { cancelled = true; };
    }, [isLoggedIn]);
    const searchParams = new URLSearchParams(location.search);
    const isReplyAll = searchParams.get('reply_all') === 'true';
    const isForward = searchParams.get('forward') === 'true';
    const action = searchParams.get('action') || 'reply'; // Default to 'reply' if not specified
    const { settings } = useSystemSettings();
    const [hasRecipients, setHasRecipients] = useState(true);
    
    // Add state for CC/BCC visibility to match MessageCompose
    const [showCcBcc, setShowCcBcc] = useState(false);
    
    // Add state for collapsible form top section
    const [formTopCollapsed, setFormTopCollapsed] = useState(false);

    // Attachment preview (for local uploads in reply)
    const [attachmentPreviewFile, setAttachmentPreviewFile] = useState(null);
    const [localFileComments, setLocalFileComments] = useState({});
    
    // Process email pattern validation
    const validateEmail = (email) => {
        if (!email || typeof email !== 'string') {
            return false;
        }
        
        // Trim any whitespace
        const trimmedEmail = email.trim();
        if (trimmedEmail === '') {
            return false;
        }
        
        // Check for basic email format - must have @ with something before and after, and a domain with at least one period
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    };

    // Fetch message data directly from API if not available in location state
    const fetchMessageData = async () => {
        setLoading(true);
        try {
            // We only need the basic message data regardless of reply type
            // The backend will use the unique_identifier to associate the reply with the original message
            let endpoint = `/api/link/reply/${unique_identifier}/`;
            
            // Add action parameter to the API request
            const params = new URLSearchParams();
            if (isReplyAll) params.append('reply_all', 'true');
            if (isForward) params.append('forward', 'true');
            params.append('action', action);
            
            // Add query params to endpoint
            if (params.toString()) {
                endpoint += `?${params.toString()}`;
            }
                
            const response = await axios.get(endpoint);
            
            if (response.data.message) {
                // Set form values first
                initializeForm(response.data.message);
                // Then update message data
                setMessageData(response.data.message);
                setRequiresAccessCode(false);
            } else {
                throw new Error(t('messages.errors.noMessageData'));
            }
        } catch (error) {
            if (error.response?.status === 401 && error.response?.data?.requires_verification) {
                setRequiresAccessCode(true);
            } else {
                console.error('Failed to fetch message data:', error);
                message.error(t('messages.errors.failedToFetch'));
                navigate(`/link/${unique_identifier}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Initialize form with message data from backend
    const initializeForm = (data) => {
        if (!data) return;
        
        // Convert comma-separated strings to arrays for Select component
        let recipientArray = [];
        if (data.recipient && typeof data.recipient === 'string') {
            recipientArray = data.recipient.split(/[,;\s]+/)
                .map(email => email.trim())
                .filter(email => email !== '');
        }
        
        let ccArray = [];
        if (data.cc && typeof data.cc === 'string') {
            ccArray = data.cc.split(/[,;\s]+/)
                .map(email => email.trim())
                .filter(email => email !== '');
        }
        
        // Set form values: message = empty (user types reply above quoted block). Quoted original is shown read-only below.
        const formValues = {
            recipient: recipientArray,
            cc: ccArray,
            bcc: [],
            subject: data.subject || '',
            message: '',
            ttl: data.timestamp ? dayjs(data.timestamp) : null
        };
        
        // Set all form values at once
        form.setFieldsValue(formValues);
        
        // Update recipient state 
        setHasRecipients(recipientArray.length > 0);
    };

    // Update form when messageData changes
    useEffect(() => {
        if (messageData && form) {
            initializeForm(messageData);
            
            // Force a reset to ensure values are displayed properly
            setTimeout(() => {
                const currentValues = form.getFieldsValue();
                form.resetFields();
                form.setFieldsValue(currentValues);
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageData, form]);

    const handleAccessCodeSuccess = () => {
        setRequiresAccessCode(false);
        setCanBecomeGuest?.(true);
        fetchMessageData();
    };

    // First check if we should fetch message data
    useEffect(() => {
        // Get basic message data - we'll format it ourselves based on operation type
        fetchMessageData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unique_identifier, form, settings]);

    // No authentication requirement for reply functionality
    // Users can reply via email links without being logged in

    if (authLoading || loading) return (
        <div className="reply-container">
            <Card className="reply-card" loading={true}>
                <div style={{ textAlign: 'center' }}>{t('messages.reply.loading')}</div>
            </Card>
        </div>
    );

    const handleSubmit = async (values) => {
        setSubmitLoading(true);
        try {
            let recipientValue = '';
            let ccValue = '';
            let bccValue = '';
            
            if (values.recipient && Array.isArray(values.recipient) && values.recipient.length > 0) {
                recipientValue = values.recipient.join(', ');
            } else if (values.recipient && typeof values.recipient === 'string' && values.recipient.trim() !== '') {
                recipientValue = values.recipient.trim();
            }
            
            if (values.cc && Array.isArray(values.cc) && values.cc.length > 0) {
                ccValue = values.cc.join(', ');
            } else if (values.cc && typeof values.cc === 'string' && values.cc.trim() !== '') {
                ccValue = values.cc.trim();
            }
            
            if (values.bcc && Array.isArray(values.bcc) && values.bcc.length > 0) {
                bccValue = values.bcc.join(', ');
            } else if (values.bcc && typeof values.bcc === 'string' && values.bcc.trim() !== '') {
                bccValue = values.bcc.trim();
            }
            
            const formData = new FormData();
            formData.append('recipient', recipientValue);
            // Append user's reply + read-only quoted original (HTML) for storage
            const fullMessage = (values.message || '') + (messageData?.reply_body || '');
            formData.append('message', fullMessage);
            formData.append('cc', ccValue);
            formData.append('bcc', bccValue);
            
            if (messageData && messageData.unique_identifier) {
                formData.append('reference_id', messageData.unique_identifier);
            }
            
            formData.append('action', action);
            if (isForward) formData.append('is_forward', 'true');
            else if (isReplyAll) formData.append('is_reply_all', 'true');

            const fileList = messageEditorRef.current?.getFileList() || [];
            fileList.forEach(file => {
                const fileToSend = file.originFileObj || file;
                if (fileToSend) formData.append('attachments', fileToSend);
            });

            if (fileList.length > 0) {
                const commentsPayload = fileList.map((f) => {
                    const comments = localFileComments[f.uid] || [];
                    return comments.map((c) => ({
                        content: c.content,
                        page: c.page,
                        position_x: c.position_x,
                        position_y: c.position_y,
                        width: c.width,
                        height: c.height,
                    }));
                });
                formData.append('attachment_comments', JSON.stringify(commentsPayload));
            }

            const response = await axios.post(`/api/send_ttl_reply/${unique_identifier}/`, formData);

            message.success('Reply sent successfully!');
            form.resetFields();
            messageEditorRef.current?.clearFileList();
            setLocalFileComments({});
            setAttachmentPreviewFile(null);

            const replyId = response.data?.unique_identifier;
            if (isFreeAccount) {
                navigate('/mailbox');
            } else if (isLoggedIn && replyId) {
                navigate(`/link/preview/${replyId}`);
            } else {
                navigate('/');
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            message.error('Failed to send reply');
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="message-reply-container">
            <Card 
                className="message-reply-card no-hover-card"
                loading={loading}
                title={
                    <div className="message-reply-header">
                        <div className="message-reply-header-left">
                            <SendOutlined className="message-reply-header-icon" />
                            <span>
                                {isForward ? "Forward Message" : 
                                 isReplyAll ? "Reply All to Message" : 
                                 "Reply to Message"}
                            </span>
                        </div>
                        <div className="message-reply-header-actions">
                            <Space>
                                <Button 
                                    type="primary" 
                                    loading={submitLoading}
                                    onClick={() => form.submit()}
                                >
                                    {t('messages.send.sendReply')}
                                </Button>
                                <Tooltip title={t('messages.send.cancel')}>
                                    <Button 
                                        type="default" 
                                        className="btn-cancel"
                                        icon={<CloseOutlined />}
                                        onClick={() => navigate(`/link/${unique_identifier}`, { 
                                            state: { preserveViews: true } 
                                        })}
                                    />
                                </Tooltip>
                            </Space>
                        </div>
                    </div>
                }
                styles={{ 
                    body: { 
                        transition: 'none',
                        background: '#ffffff'
                    },
                    root: {
                        transition: 'none',
                        background: '#ffffff'
                    }
                }}
            >
                {!loading && messageData && (
                    <Form
                        form={form}
                        onFinish={handleSubmit}
                        layout="vertical"
                        preserve={true}
                        initialValues={{
                            recipient: [],
                            cc: [],
                            bcc: [],
                            subject: '',
                            message: '',
                            ttl: null
                        }}
                    >
                        {/* Form Top Section - Collapsible */}
                        <div className={`message-reply-form-top ${formTopCollapsed ? 'collapsed' : ''}`}>
                            {/* Row 1: Recipients */}
                            <div className="message-reply-form-row">
                                <div className="message-reply-receivers-section">
                                    <Form.Item
                                        name="recipient"
                                        preserve={true}
                                    >
                                        <div className="message-reply-receiver-input-row">
                                            <div className="message-reply-recipient-field">
                                                <Select
                                                    mode="tags"
                                                    className="message-reply-input"
                                                    placeholder={isForward ? t('messages.send.recipientOptionalPlaceholder') : t('messages.send.recipientOptionalPlaceholder')}
                                                    tokenSeparators={[',', ';', ' ']}
                                                    allowClear
                                                    suffixIcon={null}
                                                    dropdownStyle={{ display: 'none' }}
                                                    disabled={!isForward}
                                                    value={form.getFieldValue('recipient')}
                                                    onChange={(value) => {
                                                        if (!isForward) return;
                                                        if (value.length > 0) {
                                                            const lastEmail = value[value.length - 1];
                                                            if (lastEmail && !validateEmail(lastEmail)) {
                                                                const filteredValues = value.filter((v, i) => i !== value.length - 1);
                                                                message.error(t('messages.validation.invalidEmail', { email: lastEmail }));
                                                                form.setFieldsValue({ recipient: filteredValues });
                                                                return;
                                                            }
                                                        }
                                                        const normalized = value
                                                            .map(item => typeof item === 'string' ? item.trim() : '')
                                                            .filter(item => item !== '' && validateEmail(item));
                                                        form.setFieldsValue({ recipient: normalized });
                                                        setHasRecipients(normalized.length > 0);
                                                        if (normalized.length === 0) {
                                                            form.setFieldsValue({ cc: [], bcc: [] });
                                                        }
                                                    }}
                                                />
                                            </div>
                                            {isForward && (
                                                <div className="message-reply-ccbcc-toggle">
                                                    <Tooltip title={!hasRecipients ? t('messages.send.addRecipientFirst') : ''}>
                                                        <div 
                                                            className={`message-reply-cc-bcc-arrow-toggle ${!hasRecipients ? 'disabled' : ''}`}
                                                            onClick={() => hasRecipients && setShowCcBcc(!showCcBcc)}
                                                        >
                                                            <DownOutlined className={`message-reply-cc-bcc-arrow ${showCcBcc ? 'rotated' : ''}`} />
                                                            <span>CC/BCC</span>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </div>
                                    </Form.Item>

                                    {isForward && (
                                        <div className={`message-reply-ccbcc-fields ${showCcBcc ? 'visible' : ''}`}>
                                            {/* CC only in overlay when no CC values (otherwise shown in main CC row) */}
                                            {!(form.getFieldValue('cc')?.length > 0 || (messageData?.cc && typeof messageData.cc === 'string' && messageData.cc.trim() !== '')) && (
                                                <Form.Item name="cc" preserve={true}>
                                                    <Select
                                                        mode="tags"
                                                        className="message-reply-input"
                                                        placeholder={t('messages.send.ccPlaceholder')}
                                                        tokenSeparators={[',', ';', ' ']}
                                                        allowClear
                                                        suffixIcon={null}
                                                        dropdownStyle={{ display: 'none' }}
                                                        disabled={!hasRecipients}
                                                        value={form.getFieldValue('cc')}
                                                        onChange={(value) => {
                                                            if (value.length > 0) {
                                                                const lastEmail = value[value.length - 1];
                                                                if (lastEmail && !validateEmail(lastEmail)) {
                                                                    const filteredValues = value.filter((v, i) => i !== value.length - 1);
                                                                    message.error(t('messages.validation.invalidEmail', { email: lastEmail }));
                                                                    form.setFieldsValue({ cc: filteredValues });
                                                                    return;
                                                                }
                                                            }
                                                            const normalized = value
                                                                .map(item => typeof item === 'string' ? item.trim() : '')
                                                                .filter(item => item !== '' && validateEmail(item));
                                                            form.setFieldsValue({ cc: normalized });
                                                        }}
                                                    />
                                                </Form.Item>
                                            )}

                                        <Form.Item name="bcc" preserve={true} style={{ marginBottom: 0 }}>
                                            <Select
                                                mode="tags"
                                                className="message-reply-input"
                                                placeholder={t('messages.send.bccPlaceholder')}
                                                tokenSeparators={[',', ';', ' ']}
                                                allowClear
                                                suffixIcon={null}
                                                dropdownStyle={{ display: 'none' }}
                                                disabled={!hasRecipients}
                                                value={form.getFieldValue('bcc')}
                                                onChange={(value) => {
                                                    if (value.length > 0) {
                                                        const lastEmail = value[value.length - 1];
                                                        if (lastEmail && !validateEmail(lastEmail)) {
                                                            const filteredValues = value.filter((v, i) => i !== value.length - 1);
                                                            message.error(t('messages.validation.invalidEmail', { email: lastEmail }));
                                                            form.setFieldsValue({ bcc: filteredValues });
                                                            return;
                                                        }
                                                    }
                                                    const normalized = value
                                                        .map(item => typeof item === 'string' ? item.trim() : '')
                                                        .filter(item => item !== '' && validateEmail(item));
                                                    form.setFieldsValue({ bcc: normalized });
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                    )}
                                </div>
                                <div className="message-reply-expiry-section">
                                    <Form.Item
                                        name="ttl"
                                    >
                                        <DatePicker
                                            showTime
                                            showNow={false}
                                            format="YYYY-MM-DD HH:mm:ss"
                                            style={{ width: '100%' }}
                                            disabled
                                            placeholder={t('messages.send.expiryPlaceholder')}
                                        />
                                    </Form.Item>
                                </div>
                            </div>

                            {/* CC recipients line - display when message has CC values, same style as recipients */}
                            {(form.getFieldValue('cc')?.length > 0 || (messageData?.cc && typeof messageData.cc === 'string' && messageData.cc.trim() !== '')) && (
                                <div className="message-reply-form-row message-reply-full-width-row">
                                    <Form.Item name="cc" preserve={true}>
                                        <div className="message-reply-receiver-input-row">
                                            <div className="message-reply-recipient-field">
                                                <Select
                                                    mode="tags"
                                                    className="message-reply-input"
                                                    placeholder={t('messages.send.ccPlaceholder')}
                                                    tokenSeparators={[',', ';', ' ']}
                                                    allowClear
                                                    suffixIcon={null}
                                                    dropdownStyle={{ display: 'none' }}
                                                    disabled={!isForward}
                                                    value={form.getFieldValue('cc')}
                                                    onChange={(value) => {
                                                        if (!isForward) return;
                                                        if (value.length > 0) {
                                                            const lastEmail = value[value.length - 1];
                                                            if (lastEmail && !validateEmail(lastEmail)) {
                                                                const filteredValues = value.filter((v, i) => i !== value.length - 1);
                                                                message.error(t('messages.validation.invalidEmail', { email: lastEmail }));
                                                                form.setFieldsValue({ cc: filteredValues });
                                                                return;
                                                            }
                                                        }
                                                        const normalized = value
                                                            .map(item => typeof item === 'string' ? item.trim() : '')
                                                            .filter(item => item !== '' && validateEmail(item));
                                                        form.setFieldsValue({ cc: normalized });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </Form.Item>
                                </div>
                            )}

                            <div className="message-reply-form-row message-reply-full-width-row">
                                <Form.Item
                                    name="subject"
                                >
                                    <Input disabled placeholder={t('messages.send.subjectPlaceholder')} />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Editor Section - editable reply above, read-only quoted original below */}
                        <div className="message-reply-editor-section">
                            <Form.Item
                                name="message"
                                rules={[{ required: true, message: t('messages.validation.messageRequired') }]}
                                className="message-editor-form-item"
                            >
                                <MessageEditor
                                    placeholder={t('messages.reply.typeAbove')}
                                    ref={messageEditorRef}
                                    attachmentsEnabled={true}
                                    allowedFileTypes={settings?.allowedFileTypes || ''}
                                    maxUploadSize={settings?.maxUploadSize || 10}
                                    showError={(msg) => message.error(msg)}
                                    formCollapsed={formTopCollapsed}
                                    onFormCollapseToggle={() => setFormTopCollapsed(!formTopCollapsed)}
                                    onAttachmentClick={(file) => {
                                        const name = file?.name || file?.originFileObj?.name || file?.file_name;
                                        if (!isConvertible(name)) return;
                                        setAttachmentPreviewFile(file);
                                    }}
                                    attachmentPreviewable={(f) => isConvertible(f.name)}
                                    fileCommentsCountMap={Object.fromEntries(
                                        Object.entries(localFileComments).map(([uid, list]) => [uid, (list || []).length])
                                    )}
                                />
                            </Form.Item>
                            {messageData?.reply_body && (
                                <div
                                    className="message-reply-quoted-original"
                                    aria-label={t('messages.reply.originalMessage')}
                                    dangerouslySetInnerHTML={{ __html: messageData.reply_body }}
                                />
                            )}
                        </div>

                    </Form>
                )}
            </Card>

            <AttachmentPreview
                open={!!attachmentPreviewFile}
                onClose={() => setAttachmentPreviewFile(null)}
                localFile={attachmentPreviewFile}
                fileName={attachmentPreviewFile?.name || attachmentPreviewFile?.originFileObj?.name || attachmentPreviewFile?.file_name}
                localComments={attachmentPreviewFile?.uid ? localFileComments[attachmentPreviewFile.uid] : undefined}
                onLocalCommentsChange={attachmentPreviewFile?.uid
                    ? (comments) => setLocalFileComments((prev) => ({ ...prev, [attachmentPreviewFile.uid]: comments }))
                    : undefined}
            />

            <AccessCodeModal
                open={requiresAccessCode}
                rid={unique_identifier}
                onSuccess={handleAccessCodeSuccess}
                onCancel={() => {
                    setRequiresAccessCode(false);
                    message.error(t('messages.view.errorAccessCodeRequired') || 'Access code required');
                    navigate(`/link/${unique_identifier}`);
                }}
            />
        </div>
    );
};

export default MessageReply;